/* eslint-disable bsky-internal/avoid-unwrapped-text */
import './LoginForm.css'

import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import type React from 'react'
import {useController} from 'react-hook-form'

import {type PasswordResetFormData} from '#/lib/schemas/password-reset'
import MailIcon from '#/components/icons/MailIcon'
import {TOKENS} from '#/wsocial/service/design-system/tokens'
import logoSvg from '../../../assets/icon-wsocial.svg'
import {type PasswordResetForm} from './types'

const T = {
  bg: TOKENS.dark.custom_background_primary,
  inputBg: '#272F44',
  textMedium: '#D0CFD7',
  accent: TOKENS.dark.positive_700,
  btnText: TOKENS.dark.custom_text_light,
  borderLow: TOKENS.dark.custom_border_contrast_low,
  negative: TOKENS.dark.negative_500,
} as const

const inputStyle: React.CSSProperties = {
  flex: 1,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  fontSize: 15,
  fontWeight: 400,
  color: '#fff',
  lineHeight: '23px',
  minWidth: 0,
}

const inputRowStyle: React.CSSProperties = {
  backgroundColor: T.inputBg,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  height: 44,
  padding: '0 12px',
  borderRadius: 10,
}

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '17px',
  color: T.textMedium,
  display: 'block',
  margin: 0,
}

export function StepForgotPassword({
  form,
  error,
  isProcessing,
  onBack,
  onNext,
  onHaveCode,
}: {
  form: PasswordResetForm
  error: string
  isProcessing: boolean
  onBack: () => void
  onNext: () => void
  onHaveCode: () => void
}) {
  const {_} = useLingui()
  const {field: emailField, fieldState: emailState} = useController<
    PasswordResetFormData,
    'email'
  >({
    control: form.control,
    name: 'email',
  })

  const canContinue = emailField.value.trim().length > 0

  const handleNext = async () => {
    const isValid = await form.trigger(['email'])
    if (isValid) onNext()
  }

  return (
    <div
      className="LoginPageShell"
      style={{
        backgroundColor: T.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: '100vh',
      }}>
      <div
        className="LoginPageShellInner"
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100%',
        }}>
        {/* Logo */}
        <div
          className="LoginLogoBlock"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <img src={logoSvg} alt="W Social" style={{width: 80, height: 80}} />
        </div>

        {/* Heading */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: '0 16px',
          }}>
          <h1
            style={{
              fontSize: 30,
              fontWeight: 700,
              lineHeight: '35px',
              color: '#fff',
              margin: 0,
            }}>
            <Trans>Forgot password</Trans>
          </h1>
          <p
            style={{
              fontSize: 13,
              fontWeight: 400,
              lineHeight: '17px',
              color: T.textMedium,
              margin: 0,
            }}>
            <Trans>Let's get your password reset!</Trans>
          </p>
        </div>

        {/* Form */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: '24px 16px',
          }}>
          <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
            <label style={labelStyle}>
              <Trans>Email</Trans>
            </label>
            <div style={inputRowStyle}>
              <MailIcon />
              <input
                type="email"
                placeholder={_(msg`Your email`)}
                value={emailField.value}
                onChange={e => emailField.onChange(e.target.value)}
                onBlur={emailField.onBlur}
                onKeyDown={e =>
                  e.key === 'Enter' && canContinue && handleNext()
                }
                disabled={isProcessing}
                autoComplete="email"
                className="SignInInput"
                style={inputStyle}
                autoFocus
              />
            </div>
            {(emailState.error?.message || error) && (
              <p
                style={{
                  color: T.negative,
                  fontSize: 13,
                  lineHeight: '17px',
                  margin: 0,
                  padding: '8px 0',
                }}>
                {emailState.error?.message || error}
              </p>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div
          style={{
            padding: '0 16px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <button
            onClick={onBack}
            disabled={isProcessing}
            className="BackButton">
            <Trans>Back</Trans>
          </button>
          <button
            onClick={handleNext}
            disabled={!canContinue || isProcessing}
            className="SignInContinueBtn"
            style={{
              backgroundColor: T.accent,
              color: T.btnText,
              fontSize: 15,
              fontWeight: 500,
              lineHeight: '20px',
              padding: '0 25px',
              height: 44,
              borderRadius: 8,
              border: 'none',
              cursor: canContinue && !isProcessing ? 'pointer' : 'not-allowed',
              opacity: canContinue && !isProcessing ? 1 : 0.6,
              whiteSpace: 'nowrap',
            }}>
            {isProcessing ? '...' : <Trans>Next</Trans>}
          </button>
        </div>

        {/* Already have a code */}
        <div style={{padding: '0 16px 8px'}}>
          <button
            onClick={onHaveCode}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 400,
              color: T.textMedium,
              padding: 0,
            }}>
            <Trans>Already have a code?</Trans>
          </button>
        </div>

        {/* Support footer */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            padding: '16px 16px',
          }}>
          <div
            style={{height: 1, backgroundColor: T.borderLow, width: '100%'}}
          />
          <p
            style={{
              fontSize: 13,
              fontWeight: 400,
              lineHeight: '17px',
              color: T.textMedium,
              margin: 0,
            }}>
            <Trans>Having trouble?</Trans>{' '}
            <a href="mailto:support@wsocial.eu" className="LinkNew">
              <Trans>Contact support</Trans>
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
